<template>
  <Card class="spot-market-bid-card no-pad">
    <template #header>
      <RateQuoteCarrier :rate-quote="bid" class="align-items-center" />

      <KeyValuePair
        :label="
          t('common.totalParam', {
            param: t('carrierPortal.spotMarket.bids'),
          })
        "
        :value="
          renderMoney(bid.totalPrice, {
            currencyCode: bid.currencyCode,
          })
        "
        juicy
      />
    </template>

    <template #content>
      <div class="bid-details border-bottom-divider">
        <h2 class="section-header">
          {{ t('common.paramId', { param: t('rateShop.quotes') }) }}:
          {{ bid.carrierQuoteNumber ?? `#${bid.id}` }}
        </h2>

        <div class="row">
          <KeyValuePair :label="t('shipments.modes')" :value="mode" />

          <KeyValuePair
            :label="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            :value="bid.equipmentType?.name"
          />

          <KeyValuePair
            :label="t('carriers.serviceLevels')"
            :value="bid.serviceType"
          />

          <KeyValuePair
            :label="t('rateShop.transit')"
            :value="
              bid.transitTime
                ? t('rateShop.countDays', { count: bid.transitTime })
                : ''
            "
          />
        </div>
      </div>

      <div class="charge-row border-bottom-divider">
        <KeyValuePair
          :label="t('shipments.financials.linehaul')"
          :value="
            renderMoney(bid.rawLineHaulNetCharge, {
              currencyCode: bid.currencyCode,
            })
          "
          inline
        />

        <KeyValuePair
          :label="t('shipments.financials.fuel')"
          :value="
            renderMoney(bid.fuelSurchargeAmount, {
              currencyCode: bid.currencyCode,
            })
          "
          inline
        />
      </div>

      <div
        v-if="bid.accessorials.length"
        class="charge-row border-bottom-divider"
      >
        <h3>
          {{
            t('common.additionalParam', { param: t('shipments.services', 2) })
          }}
        </h3>

        <SpotMarketBidCardAccessorial
          v-for="(accessorial, i) in bid.accessorials"
          :key="i"
          :accessorial="accessorial"
          :currency-code="bid.currencyCode"
        />
      </div>
    </template>

    <template #footer>
      <RygenButton
        mode="secondary"
        :label="
          t('common.cancelParam', {
            param: t('carrierPortal.spotMarket.bids'),
          })
        "
        :severity="Severity.DANGER"
        :disabled="disabled"
        @click="emit('delete', bid)"
      />

      <RygenButton
        v-if="showNewBidButton"
        mode="primary"
        icon="pi pi-dollar"
        :label="t('carrierPortal.spotMarket.placeAnotherBid')"
        :disabled="disabled"
        @click="emit('new-bid')"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney, translateEnum } from '@rygen/rygen-ui/i18n';
import { Severity } from '@rygen/rygen-ui/model';

import { RateQuote } from '@/model';

import SpotMarketBidCardAccessorial from '@/components/carrierPortal/rateShop/SpotMarketBidCardAccessorial.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';

const props = defineProps({
  bid: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  showNewBidButton: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { bid, showNewBidButton, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'delete', rateQuote: RateQuote): void;
  (event: 'new-bid'): void;
}>();

const { t } = useI18n();

const mode = computed(() =>
  bid.value.mode ? translateEnum(bid.value.mode, 'Mode') : '',
);
</script>

<style>
.spot-market-bid-card.p-card {
  .p-card-header {
    display: flex;
    justify-content: space-between;
    gap: var(--sp-3);
    padding: var(--sp-6);
    border-bottom: var(--divider);
  }

  .p-card-body {
    gap: 0;

    .p-card-content {
      .bid-details {
        display: flex;
        flex-flow: column;
        gap: var(--sp-3);
        padding: var(--sp-6);

        .section-header {
          margin-bottom: var(--sp-3);
        }

        .row {
          display: flex;
          gap: var(--sp-3);

          .key-value-pair {
            width: 25%;

            .key-value-pair-label {
              color: var(--p-gray-400);
              font-size: 14px;
              font-weight: 400;
            }

            &:not(:last-of-type) {
              border-right: var(--divider);
              padding-right: var(--sp-3);
              margin-right: var(--sp-3);
            }
          }
        }
      }

      .charge-row {
        display: flex;
        flex-direction: column;
        gap: var(--sp-3);
        padding: var(--sp-6);
      }
    }

    .p-card-footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--sp-3);
      padding: var(--sp-6);
    }
  }

  .border-bottom-divider {
    border-bottom: var(--divider);
  }
}
</style>
